/**
 * What a student may call themselves for the length of one lesson.
 *
 * A name exists so a teacher looking at thirty tiles sees people rather than
 * inventory. It lives in the running session and nowhere else (ADR-021): it
 * is never written to a row, a cache or a headset's storage, and the export a
 * teacher downloads tomorrow names no child.
 *
 * The rule lives here, beside the lesson format and the trouble codes, for the
 * same reason those do: both ends have to agree. The headset will not let a
 * student into VR without a name, and the API decides what it keeps -- and if
 * those answers differ, a child types something the headset accepts, the
 * server throws it away, and the teacher's grid quietly shows a headset name.
 */
/**
 * The longest name we keep.
 *
 * Long enough for "Jan Willem van der Berg" to survive recognisably, short
 * enough to fit a tile on a grid of thirty. Anything longer is cut rather
 * than refused: a child who holds a key down should be let into the lesson,
 * not stopped at the door.
 */
export const STUDENT_NAME_MAX = 40;
/**
 * Tidies what a child typed with a headset on.
 *
 * Trimmed and collapsed rather than validated. A ten-year-old typing on a
 * virtual keyboard produces stray spaces and the odd control character, and
 * an error message in front of a class helps nobody -- so whatever arrives is
 * made safe and accepted. Control characters go because this ends up on a
 * teacher's screen, and a newline in the middle of a grid tile is somebody
 * else's bug report.
 *
 * Returns an empty string when nothing usable was typed, which is what
 * `hasStudentName` reads.
 */
export function cleanStudentName(value) {
    return (value
        // Written as escapes rather than typed: control characters are exactly
        // what this strips, and a literal one in the source is invisible.
        // eslint-disable-next-line no-control-regex
        .replace(/[ -]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, STUDENT_NAME_MAX));
}
/**
 * Whether a student has actually given a name.
 *
 * The question the headset's gate asks. Whitespace is not a name: a child who
 * taps the field, hits the space bar and expects to be let through is the
 * case this exists for.
 */
export function hasStudentName(value) {
    return cleanStudentName(value).length > 0;
}
//# sourceMappingURL=student-name.js.map